import React, { Fragment } from "react";
import { makeStyles } from "@mui/styles";
import { Typography, Button, CircularProgress } from "@mui/material";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import GetAppIcon from "@mui/icons-material/GetApp";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import colors from "../../../../utils/colors";
import theme from "../../../theme";
import * as _ from "lodash";

const useStyles = makeStyles({
	octaBtnStyles: {
		borderRadius: "50px",
		// padding: "6px 30px",
		height: "fit-content",
		alignSelf: "center",
		margin: "2px",
		"& span": {
			textTransform: "initial"
		}
	},
	octaBtnRed: {
		background: colors.octaRed,
		"&:hover": {
			cursor: "pointer",
			background: colors.octaRedInk
		},
		"& span": {
			color: colors.white
		}
	},
	octaBtnBlue: {
		background: colors.octaBlue,
		"&:hover": {
			cursor: "pointer",
			background: colors.octaBlueInk
		},
		"& span": {
			color: colors.white
		}
	},
	octaBtnWhite: {
		background: colors.white,
		"&:hover": {
			cursor: "pointer"
		},
		"& span": {
			color: `${colors.octaBlue} !important`
		}
	},
	octaBtnTransparent: {
		"& button": {
			background: colors.white,
			"&:hover": {
				cursor: "pointer",
				background: colors.white
			}
		},
		"&:hover": {
			background: colors.white
		},
		"& span": {
			color: colors.octaBlue,
			textDecoration: "underline"
		},
		"& svg": {
			width: "20px",
			height: "20px"
		},
		"& > span > span": {
			margin: "0 5px",
			textAlign: "justify"
			// fontSize: "18px"
		}
	},
	octaBtnInverted: {
		background: colors.transparent,
		"& button": {
			background: colors.transparent,
			"&:hover": {
				cursor: "pointer",
				background: colors.transparent
			}
		},
		"&:hover": {
			background: colors.transparent
		},
		"& span": {
			color: colors.white,
			textDecoration: "underline"
		},
		"& svg": {
			width: "20px",
			height: "20px"
		},
		"& > span > span": {
			margin: "0 5px",
			textAlign: "justify"
			// fontSize: "18px"
		}
	},
	btnTypography: {
		[theme.breakpoints.down('md')]: {
			fontSize: "13px"
		}
	},
	buttonProgress: {
		color: colors.octaBlue,
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12
	},
	largeButton: {
		padding: "13px 60px"
	},
	replayIcon: {
		fontSize: "20px"
	},
	"@keyframes rotatingLeft": {
		"from": {
			"-webkit-transform": "rotate(0deg)",
			"-o-transform": "rotate(0deg)",
			transform: "rotate(0deg)"
		},
		"to": {
			"-webkit-transform": "rotate(-360deg)",
			"-o-transform": "rotate(-360deg)",
			transform: "rotate(-360deg)"
		}
	},
	refreshIcon: {
		fontSize: "20px"
	},
	"@keyframes rotatingRight": {
		"from": {
			"-webkit-transform": "rotate(0deg)",
			"-o-transform": "rotate(0deg)",
			transform: "rotate(0deg)"
		},
		"to": {
			"-webkit-transform": "rotate(360deg)",
			"-o-transform": "rotate(360deg)",
			transform: "rotate(360deg)"
		}
	}
});

interface OctaButtonProps {
	value: string;
	mode: string;
	action?: string;
	withIcon: boolean;
	handleClick: any;
	disabled?: boolean;
	isLoading?: boolean;
	size?: "small" | "medium" | "large";
}

const OctaButton = ({
	value,
	mode,
	action,
	withIcon,
	handleClick,
	disabled,
	isLoading,
	size
}: OctaButtonProps) => {
	const classes = useStyles();
	const { t } = useTranslation("common");

	return (
		<Button
			variant={mode === "neutral" ? "text" : "contained"}
			aria-label={action}
			size={size || "medium"}
			classes={{
				textSizeLarge: classes.largeButton
			}}
			className={cx({
				[classes.octaBtnStyles]: true,
				[classes.octaBtnRed]: mode === "red",
				[classes.octaBtnBlue]: mode === "dark",
				[classes.octaBtnWhite]: mode === "light",
				[classes.octaBtnTransparent]: mode === "neutral",
				[classes.largeButton]: size === "large",
				[classes.octaBtnInverted]: mode === "inverted"
			})}
			onClick={handleClick}
			disabled={disabled || false}
		>
			{withIcon && action === "add" ? (
				<Fab size="small" aria-label={action}>
					<AddIcon />
				</Fab>
			) : null}
			{withIcon && action === "edit" ? <EditIcon /> : null}
			{withIcon && action === "back" ? <ArrowBackIosIcon /> : null}
			{withIcon && action === "download" ? <GetAppIcon /> : null}
			{withIcon && action === "filter" ? <FilterListIcon /> : null}
			{withIcon && action === "refresh" ? <RefreshIcon className={classes.refreshIcon} /> : null}
			{withIcon && action === "replay" ? <ReplayIcon className={classes.replayIcon} /> : null}
			{withIcon && action === "mail" ? <MailOutlineIcon /> : null}
			<Typography variant="button" className={classes.btnTypography}>
				{isLoading && isLoading === true ? (
					<>
						<CircularProgress size={24} className={classes.buttonProgress} />{" "}
						{_.upperFirst(t(value))}
					</>
				) : (
					_.upperFirst(t(value))
				)}
			</Typography>
			{withIcon && action === "next" ? <ArrowForwardIosIcon /> : null}
		</Button>
	);
};

export default OctaButton;
